import { PaymentPlan } from '../../core/models/property.model';
import { propertyStatusKey } from './property-status.util';
import {
    StyledSheetColumn,
    StyledSheetImage,
    StyledSheetOptions,
    StyledSheetTheme,
    StyledSheetTitleCell
} from './styled-workbook.util';

/**
 * Properties "Full Report" export, shaped to the template the sales team supplied
 * (`Full Report nibni templete  - Sheet2.pdf` in the repo root).
 *
 * That template is a PDF, so every number below was MEASURED off it rather than
 * read out of a workbook — `Full Report nibni templete  - Sheet2.pdf` is a
 * Google Sheets print at 80.8% ("fit to width" on US Letter landscape), which is
 * the scale at which three independent quantities land on Google's own defaults:
 * the masthead rows come out at 21px, the "Generated by" line at 11pt and the
 * table's own font at ~7.6pt. Everything here is that measurement divided by
 * 0.808 and converted to Excel units (px = width * 7 + 5 for Calibri 11).
 *
 * Deliberate deviations from the template, all for the same reason — it is a
 * print of a Google sheet, so some traits are artifacts:
 *   - the table font is Excel's 8pt rather than the measured ~7.6pt, with the
 *     11.25pt row height that goes with it (the template's 14px rows would clip
 *     descenders at 8pt);
 *   - the three payment-plan headers are derived from each plan's own term, so a
 *     project on 5/7/10-year plans reads "5 YEARS / 7 YEARS / 10 YEARS" instead of
 *     the template's hard-coded 6/7/8;
 *   - row numbering uses `ROW()`, which needs no dynamic-array support;
 *   - the template has no totals row, so the .xlsx has none either. The .csv keeps
 *     the one it has always had.
 */

/**
 * Near-white row separators, black column rules, dark-grey header — the template's
 * own frame. Measured: header fill #434343, row separators #F3F3F3, column rules
 * and outer frame black.
 */
export const PROPERTY_REPORT_THEME: StyledSheetTheme = {
    headerBg: '434343',
    headerText: 'FFFFFF',
    bandBg: '',
    gridline: 'F3F3F3',
    columnLine: '000000',
    headerLine: 'F3F3F3',
    headerBottomLine: '000000',
    edge: '000000'
};

/**
 * The template's status colours (Google Sheets' own "light green 3" / "light red 3"
 * and a tan). Sold and Contracted share one fill in the template — that is not a
 * transcription slip, the PDF contains a single tan fill covering both blocks.
 */
export const PROPERTY_STATUS_SHEET_FILLS: Readonly<Record<string, string>> = {
    available: 'D9EAD3',
    reserved: 'F4CCCC',
    reservedwithdeposit: 'F4CCCC',
    sold: 'D8C79B',
    contracted: 'D8C79B'
};

/** Sheet row holding the column headers; rows 1–7 are the masthead and 8–9 the gap. */
export const PROPERTY_SHEET_HEADER_ROW = 10;

/** 0-based index of the Status column, whose fill carries the status colour. */
export const PROPERTY_STATUS_COLUMN_INDEX = 2;

/** Point size of the header and body text. */
export const PROPERTY_SHEET_FONT_SIZE = 8;

/** Excel's natural row height for 8pt Calibri; the template measures 14px/10.5pt. */
export const PROPERTY_SHEET_ROW_HEIGHT = 11.25;

/** Default row height of a Google sheet (21px), which the masthead rows keep. */
const MASTHEAD_ROW_HEIGHT = 15.75;

/**
 * The Leads column holds comma-joined names that can run far past the template's
 * samples. It wraps, and the rows it sits in are sized below.
 */
const WRAPPING_COLUMNS: readonly { index: number; charsPerLine: number }[] = [
    { index: 10, charsPerLine: 44 }
];

/** One pathological cell must not turn a row into a page. */
const MAX_WRAPPED_LINES = 10;

/** The template's status wording. `propertyStatusLabel` says "Reserved / Hold". */
export function sheetStatusForProperty(status: unknown): string {
    switch (propertyStatusKey(status)) {
        case 'available': return 'Available';
        case 'reserved': return 'Reserved';
        case 'reservedwithdeposit': return 'Reserved with deposit';
        case 'sold': return 'Sold';
        case 'contracted': return 'Contracted';
        case 'pendingapproval': return 'Pending Approval';
        case 'unpublished': return 'Unpublished';
        case 'draft': return 'Draft';
        default: return '';
    }
}

/** Status cell → its fill, or null for the statuses the template has no colour for. */
export function propertyStatusSheetFill(status: unknown): string | null {
    return PROPERTY_STATUS_SHEET_FILLS[propertyStatusKey(status)] ?? null;
}

/**
 * Lead statuses that mean the person actually HOLDS the unit. The properties
 * sheet's "Leads" column lists only holders — pipeline leads (new, approached,
 * qualified, …) are sales working the person FOR the unit, not on it, and
 * printing them next to the holder makes a closed unit look contested
 * (client report 2026-09-16: a "new" lead on a reserved-with-deposit unit).
 * Raw lead.status uses reservations.status codes (lowercase, underscores).
 */
const LEAD_HOLD_STATUSES: ReadonlySet<string> = new Set([
    'reserved',
    'reserved_with_deposit',
    'sold',
    'contracted'
]);

export function isHoldLeadStatus(status: unknown): boolean {
    return LEAD_HOLD_STATUSES.has(String(status || '').trim().toLowerCase());
}

const PROPERTY_REPORT_STATUS_ORDER: Readonly<Record<string, number>> = {
    available: 0,
    reserved: 1,
    reservedwithdeposit: 2,
    sold: 3,
    contracted: 4,
    draft: 5,
    unpublished: 5,
    pendingapproval: 5,
    unknown: 6
};

export function propertyReportStatusRank(status: unknown): number {
    return PROPERTY_REPORT_STATUS_ORDER[propertyStatusKey(status)] ?? PROPERTY_REPORT_STATUS_ORDER['unknown'];
}

/**
 * The only per-cell fill in the report: the Status column carries the status colour
 * and nothing else is tinted. Wired into the options below so a caller cannot ship
 * the layout without the colours.
 */
export function propertyReportCellFill(_rowIndex: number, colIndex: number, value: unknown): string | null {
    return colIndex === PROPERTY_STATUS_COLUMN_INDEX ? propertyStatusSheetFill(value) : null;
}

/**
 * `6 YEARS` — the template names each payment-plan column after the plan's term,
 * so the header has to come from the data rather than a constant.
 */
export function propertyPlanColumnHeader(plan: PaymentPlan | null | undefined, index: number): string {
    const months = Number(plan?.totalPeriod);
    if (Number.isFinite(months) && months >= 12) {
        const years = Math.round(months / 12);
        return `${years} ${years === 1 ? 'YEAR' : 'YEARS'}`;
    }
    const label = String(plan?.label || '').trim();
    return label ? label.toUpperCase() : `PLAN ${index + 1}`;
}

/**
 * A-K follows the template order with Assigned and Partner removed.
 * `Garden( SQM)` keeps
 * the template's spacing verbatim. Every column is centred there, including the
 * text ones, and the row number is bold.
 *
 * Measured widths in px → Excel units: 28, 168, 168, 24, 50, 54, 71, 55, 55, 55,
 * 46, 156, 209 (the Partner width is intentionally omitted).
 */
export function buildPropertyReportColumns(planHeaders: readonly string[]): StyledSheetColumn[] {
    const plans = [0, 1, 2].map((index) => String(planHeaders[index] ?? `PLAN ${index + 1}`));
    return [
        { header: '#', width: 3.29, align: 'center', rowNumber: true, bold: true },
        { header: 'Unit ID', width: 23.29, align: 'center' },
        { header: 'Status', width: 23.29, align: 'center' },
        { header: 'BUA', width: 2.71, align: 'center', numFmt: '#,##0' },
        { header: 'Bedrooms', width: 6.43, align: 'center', numFmt: '#,##0' },
        { header: 'Bathrooms', width: 7.0, align: 'center', numFmt: '#,##0' },
        { header: 'Garden( SQM)', width: 9.43, align: 'center', numFmt: '#,##0' },
        { header: plans[0], width: 7.14, align: 'center', numFmt: '#,##0' },
        { header: plans[1], width: 7.14, align: 'center', numFmt: '#,##0' },
        { header: plans[2], width: 7.14, align: 'center', numFmt: '#,##0' },
        { header: 'Leads', width: 29.14, align: 'center', autoRtl: true, wrap: true }
    ];
}

/**
 * How tall each data row has to be for its wrapped cells to be fully visible.
 *
 * This is computed rather than left to the application's auto-fit on purpose: exceljs
 * writes `customHeight="1"` on `<sheetFormatPr>` alongside a custom `defaultRowHeight`,
 * and a reader is then entitled to treat that default as authoritative for any row with
 * no `ht` of its own — which would clip wrapped text instead of growing the row. Sizing
 * the rows here is deterministic in Excel, LibreOffice and Google Sheets alike, and it
 * keeps every short row at the template's compact height.
 */
export function propertyReportRowHeights(
    rows: readonly unknown[][],
    firstRow: number
): Record<number, number> {
    const heights: Record<number, number> = {};
    rows.forEach((row, index) => {
        const lines = WRAPPING_COLUMNS.reduce((most, column) => {
            const text = String(row?.[column.index] ?? '');
            if (!text) return most;
            return Math.max(most, Math.ceil(text.length / column.charsPerLine));
        }, 1);
        if (lines <= 1) return;
        heights[firstRow + index] = Math.min(lines, MAX_WRAPPED_LINES) * PROPERTY_SHEET_ROW_HEIGHT;
    });
    return heights;
}

export interface PropertyReportBranding {
    /** Developer/company name — the masthead's first line and its "Generated by". */
    orgName?: string | null;
    /** One project's name, or "All Projects" when the export spans several. */
    projectLabel?: string | null;
    /** Free text under the project; omitted from the masthead when empty. */
    location?: string | null;
    /** Defaults to today. */
    exportDate?: Date | null;
    /** Company logo for the A1:C2 block. */
    developerLogo?: Omit<StyledSheetImage, 'column' | 'row' | 'offsetX' | 'offsetY'> | null;
    /** Project cover, top-right over the final report columns. */
    projectCover?: Omit<StyledSheetImage, 'column' | 'row' | 'offsetX' | 'offsetY'> | null;
    /** nibni logo, placed after "Powered By" below the last row. */
    nibniLogo?: Omit<StyledSheetImage, 'column' | 'row' | 'offsetX' | 'offsetY'> | null;
}

/** Pixel boxes the three template images are drawn in, measured off the PDF. */
export const PROPERTY_SHEET_IMAGE_BOXES = {
    developerLogo: { maxWidth: 94, maxHeight: 86 },
    projectCover: { maxWidth: 275, maxHeight: 153 },
    nibniLogo: { maxWidth: 50, maxHeight: 17 }
} as const;

/** `19 Aug 2026`, the template's own date wording. */
function formatMastheadDate(date: Date): string {
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${date.getDate()} ${month} ${date.getFullYear()}`;
}

function sheetText(value: unknown): string {
    const text = String(value ?? '').trim();
    if (!text || text === '—' || text === '-') return '';
    return text;
}

/**
 * The masthead, footer, row heights and image anchors that turn the bare table into
 * the template. The rows are needed because "Powered By" and the nibni mark sit in
 * the row immediately below the last unit, and because the two wrapping columns
 * decide how tall each data row has to be.
 */
export function buildPropertyReportOptions(
    rows: readonly unknown[][],
    branding: PropertyReportBranding = {}
): StyledSheetOptions {
    const orgName = sheetText(branding.orgName);
    const projectLabel = sheetText(branding.projectLabel) || 'All Projects';
    const location = sheetText(branding.location);
    const exportDate = branding.exportDate instanceof Date ? branding.exportDate : new Date();
    const footerRow = PROPERTY_SHEET_HEADER_ROW + rows.length + 1;

    const images: StyledSheetImage[] = [];
    if (branding.developerLogo?.data) {
        images.push({ ...branding.developerLogo, column: 1, row: 1, offsetX: 2, offsetY: 2 });
    }
    if (branding.projectCover?.data) {
        // Keep the cover aligned to the top-right Leads column.
        images.push({ ...branding.projectCover, column: 11, row: 1, offsetX: 47, offsetY: 1 });
    }
    if (branding.nibniLogo?.data) {
        // Place the mark just beyond the final table column so it trails the
        // right-aligned "Powered By" text without obscuring it.
        images.push({ ...branding.nibniLogo, column: 12, row: footerRow, offsetX: 98, offsetY: 4 });
    }

    // Written as a list and then laid onto consecutive rows, so an org with no
    // location (or no name on file) closes the gap rather than leaving a hole and
    // shifting the table down.
    const lines: Omit<StyledSheetTitleCell, 'ref'>[] = [
        ...(orgName ? [{ text: orgName, bold: true, fontSize: 12 }] : []),
        { fontSize: 12, richText: [{ text: 'Project : ', bold: true }, { text: projectLabel }] },
        ...(location
            ? [{ fontSize: 12, richText: [{ text: 'Location :', bold: true }, { text: ` ${location}` }] }]
            : []),
        { fontSize: 12, richText: [{ text: 'Date: ', bold: true }, { text: formatMastheadDate(exportDate) }] },
        ...(orgName ? [{ text: `Generated by ${orgName}`, bold: false, fontSize: 11 }] : [])
    ];

    const titleCells: StyledSheetTitleCell[] = [
        { ref: 'A1:C2' },
        ...lines.map((line, index) => ({ ...line, ref: `A${3 + index}:C${3 + index}` })),
        {
            ref: `K${footerRow}`,
            text: 'Powered By',
            bold: true,
            fontSize: PROPERTY_SHEET_FONT_SIZE,
            align: 'right',
            vertical: 'bottom'
        }
    ];

    return {
        theme: PROPERTY_REPORT_THEME,
        headerRow: PROPERTY_SHEET_HEADER_ROW,
        fontSize: PROPERTY_SHEET_FONT_SIZE,
        defaultRowHeight: PROPERTY_SHEET_ROW_HEIGHT,
        // The template prints without Excel's own grid, and the masthead only reads
        // as a masthead on plain white.
        showGridLines: false,
        rowHeights: {
            // 21px + 65px = the 86px band the company logo sits in.
            2: 53.25,
            3: MASTHEAD_ROW_HEIGHT,
            4: MASTHEAD_ROW_HEIGHT,
            5: MASTHEAD_ROW_HEIGHT,
            6: MASTHEAD_ROW_HEIGHT,
            7: MASTHEAD_ROW_HEIGHT,
            8: MASTHEAD_ROW_HEIGHT,
            9: MASTHEAD_ROW_HEIGHT,
            ...propertyReportRowHeights(rows, PROPERTY_SHEET_HEADER_ROW + 1),
            [footerRow]: MASTHEAD_ROW_HEIGHT
        },
        titleCells,
        images,
        cellFill: propertyReportCellFill
    };
}
